import moment from 'moment';
import { DaysOfWeek } from './request';

interface WeekInfo {
    year: number;
    weekNumber: number;
}

export function getWeekNumbersBetweenDates(startDate: moment.Moment, endDate: moment.Moment): WeekInfo[] {
    // Ensure the dates are in the correct order
    const start = moment.min(startDate, endDate);
    const end = moment.max(startDate, endDate);

    const weekNumbers: WeekInfo[] = [];

    // Current week to track
    let currentWeek = start.clone().startOf('week');

    // Continue until we pass the end date
    while (currentWeek.isSameOrBefore(end)) {
        weekNumbers.push({
            year: currentWeek.weekYear(), // Use weekYear to handle week-based year
            weekNumber: currentWeek.week()
        });

        // Move to the next week
        currentWeek.add(1, 'week');
    }

    return weekNumbers;
}

export function getIsoWeekDates(year: number, weekNumber: number): moment.Moment[] {
    const startOfWeek = moment().isoWeekYear(year).isoWeek(weekNumber).startOf('isoWeek');
    return Array.from({ length: 7 }, (_, i) => startOfWeek.clone().add(i, 'days'));
}

// A Request may restrict which weekdays it can be clocked on - days not in daysOfWeek are not
// scheduled at all, so every clock-editing view (employee, employer, admin) must exclude them
// from the week's day columns, not merely disable them.
export function getScheduledIsoWeekDates(year: number, weekNumber: number, daysOfWeek: DaysOfWeek[]): moment.Moment[] {
    const weekDates = getIsoWeekDates(year, weekNumber);
    if (daysOfWeek.length === 0) {
        return weekDates;
    }

    return weekDates.filter((date) => daysOfWeek.includes(Math.pow(2, date.isoWeekday() - 1)));
}

// moment(undefined) silently means "now" and moment(null) is an invalid date, so a missing
// registration bound would otherwise turn into "today" and block every other day of the week.
// An absent or unparseable bound has to stay absent instead, leaving that side unbounded.
export function toRegistrationBound(value: string | Date | null | undefined): moment.Moment | null {
    if (value == null) {
        return null;
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
}

// A registration's own start/end date may fall mid-week, so a day within the selected ISO
// week can still be outside the period the registration actually covers.
export function isDateOutsideRegistrationRange(date: moment.Moment, registrationStart: moment.Moment | null, registrationEnd: moment.Moment | null): boolean {
    return (registrationStart != null && date.isBefore(registrationStart, 'day'))
        || (registrationEnd != null && date.isAfter(registrationEnd, 'day'));
}
